
import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SettingsSearchContext } from './SettingsContext';
import { Lock, Crown, ChevronDown, LucideIcon, Sparkles } from 'lucide-react';
import { InfoPopup } from './InfoPopup';
import { SETTING_INFO } from '@/data/settingInfo';
import { STUDIO_THEME } from './StudioTheme';

interface CollapsibleSectionProps {
  title: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  darkMode: boolean;
  defaultOpen?: boolean;
  className?: string;
  isLocked?: boolean;
  lockRequiredPlan?: 'FREE' | 'PRO' | 'ELITE' | 'ENTERPRISE';
  onUpgradeClick?: () => void;
  settingKey?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, icon: Icon, children, darkMode, defaultOpen = false, className = "", isLocked = false, lockRequiredPlan = "PRO", onUpgradeClick, settingKey
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const searchQuery = useContext(SettingsSearchContext);

  const effectiveOpen = searchQuery ? true : isOpen;

  const info = settingKey ? SETTING_INFO[settingKey] : null;
  const tooltip = info?.description || '';

  const planColors = {
    FREE: 'text-zinc-500 border-zinc-200 bg-zinc-50',
    PRO: 'text-cyan-600 border-cyan-200 bg-cyan-50 shadow-[0_0_10px_rgba(6,182,212,0.1)]',
    ELITE: 'text-purple-600 border-purple-200 bg-purple-50 shadow-[0_0_10px_rgba(168,85,247,0.1)]',
    ENTERPRISE: 'text-pink-600 border-pink-200 bg-pink-50 shadow-[0_0_10px_rgba(236,72,153,0.1)]',
  };

  const planIcons = {
    FREE: Sparkles,
    PRO: Crown,
    ELITE: Crown,
    ENTERPRISE: Crown,
  };

  const PlanIcon = planIcons[lockRequiredPlan as keyof typeof planIcons] || Crown;

  return (
    <div className={`w-full flex flex-col relative ${className}`}>
      <button 
        onClick={() => !searchQuery && setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 py-4 px-1 group transition-all ${searchQuery ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className={`flex-1 h-px transition-all ${darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-zinc-950/5 group-hover:bg-zinc-950/10'}`} />
        <div className="flex items-center gap-2.5 px-3 relative">
            <div className="flex items-center gap-2.5">
                {Icon && <Icon className={`w-3.5 h-3.5 ${darkMode ? 'text-white/20 group-hover:text-white/60' : 'text-zinc-950/20 group-hover:text-zinc-950/60'} transition-all`} />}
                <span className={`${STUDIO_THEME.typography.sectionLabel} ${darkMode ? 'text-white/40 group-hover:text-white/80' : 'text-zinc-950/40 group-hover:text-zinc-950/80'}`}>
                    {title}
                </span>
                
                {tooltip && (
                   <div onClick={(e) => e.stopPropagation()}>
                    <InfoPopup content={tooltip} darkMode={darkMode} />
                   </div>
                )}
                
                {/* Plan Badge - Always visible if requested or linked to lock */}
                <div className={`flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full border text-[8px] font-medium uppercase tracking-widest transition-all ${planColors[lockRequiredPlan as keyof typeof planColors] || planColors.PRO}`}>
                    <PlanIcon className="w-2 h-2" />
                    <span>{lockRequiredPlan}</span>
                </div>
            </div>
            {!searchQuery && (
                <motion.div 
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ type: 'spring', damping: 15 }}
                    className="opacity-10 ml-2"
                >
                    <ChevronDown className="w-3 h-3" />
                </motion.div>
            )}
        </div>
        <div className={`flex-1 h-px transition-all ${darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-zinc-950/5 group-hover:bg-zinc-950/10'}`} />
      </button>

      <AnimatePresence initial={false}>
        {effectiveOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-6 pt-1 px-1 relative @container">
                <div className={isLocked ? 'pointer-events-none transition-all duration-500' : ''}>
                    <div className={isLocked ? 'opacity-70 grayscale-[0.2] blur-none' : ''}>
                        {children}
                    </div>
                </div>

                {isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4 z-20 overflow-hidden">
                         <div className={`absolute inset-0 ${darkMode ? 'bg-zinc-950/40' : 'bg-white/40'} backdrop-blur-[2px] rounded-2xl`} />
                         
                         <motion.button 
                             initial={{ scale: 0.95, opacity: 0 }}
                             animate={{ scale: 1, opacity: 1 }}
                             whileHover={{ scale: 1.02 }}
                             whileTap={{ scale: 0.98 }}
                             onClick={(e) => {
                                 e.stopPropagation();
                                 onUpgradeClick?.();
                             }}
                             className={`relative z-10 flex flex-col items-center gap-2 px-4 py-3 sm:px-8 sm:py-6 rounded-2xl sm:rounded-3xl border transition-all group shadow-2xl overflow-hidden min-w-[120px] max-w-[calc(100%-1rem)] max-h-[calc(100%-1rem)] ${
                                 darkMode 
                                 ? 'bg-zinc-900/95 border-white/10 text-white shadow-black/60' 
                                 : 'bg-white/95 border-zinc-200 text-zinc-950 shadow-zinc-950/20'
                             } backdrop-blur-xl`}
                         >
                             {/* Ambient Glow */}
                             <div className={`absolute -top-10 -left-10 w-24 h-24 blur-[40px] opacity-20 rounded-full transition-all group-hover:opacity-40 pointer-events-none ${
                                 lockRequiredPlan === 'PRO' ? 'bg-cyan-500' : 
                                 lockRequiredPlan === 'ELITE' ? 'bg-purple-500' :
                                 'bg-pink-500'
                             }`} />

                             <div className={`w-8 h-8 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center transition-all shrink-0 ${
                                 lockRequiredPlan === 'PRO' ? 'bg-cyan-500/10 text-cyan-400 group-hover:bg-cyan-500/20' : 
                                 lockRequiredPlan === 'ELITE' ? 'bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20' :
                                 'bg-pink-500/10 text-pink-400 group-hover:bg-pink-500/20'
                             }`}>
                                 <Lock className="w-4 h-4 sm:w-6 sm:h-6" />
                             </div>
                             
                             <div className="flex flex-col items-center min-w-0">
                                 <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] opacity-40 mb-0.5 sm:mb-1 whitespace-nowrap">Feature Locked</span>
                                 <h4 className="text-[10px] sm:text-sm font-black uppercase tracking-tight italic whitespace-nowrap truncate max-w-full">
                                    Upgrade to {lockRequiredPlan}
                                 </h4>
                             </div>

                             {/* Shimmer effect */}
                             <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none" />
                         </motion.button>
                    </div>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
